import type { Model } from '#/graphql/types';
import { gql } from '@apollo/client';
import {
  getAssetType,
  getAssetTypeList,
  getManufacturer,
  getManufacturerList,
} from '#/graphql/queries';
import { inUseStatuses, severity } from './constants';
import { getBaseColumns } from '#/common/utils/baseColumns';
import FieldDefs from '#/types/FieldDefs';

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID Number',
    editorType: 'TextBox',
    editorOptions: {
      readOnly: true,
    },
    isRequired: true,
  },

  name: {
    dataField: 'name',
    label: 'Model Name',
    isRequired: true,
    editorType: 'TextBox',
  },
  assetTypeId: {
    dataField: 'assetTypeId',
    label: 'Asset Type',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      button: {
        tooltip: 'View asset type details',
        url: '/assets/types/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      searchEnabled: true,
      subtitleExpr: 'category',
      dataStoreOptions: {
        getItem: gql(getAssetType),
        getItemList: gql(getAssetTypeList),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
    },
  },
  manufacturerId: {
    dataField: 'manufacturerId',
    label: 'Manufacturer',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      button: {
        tooltip: 'View manufacturer details',
        url: '/assets/manufacturers/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      searchEnabled: true,
      subtitleExpr: 'contactPerson',
      dataStoreOptions: {
        getItem: gql(getManufacturer),
        getItemList: gql(getManufacturerList),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
    },
  },
  oemPartNumber: {
    dataField: 'oemPartNumber',
    label: 'OEM Part Number',
    isRequired: false,
    editorType: 'TextBox',
  },
  probability: {
    dataField: 'probability',
    label: 'Probability',
    isRequired: false,
    editorType: 'NumberBox',
    editorOptions: {
      format: '#0.#',
      placeholder: 'Enter a number',
      min: 0,
      max: 1,
      step: 0.1,
      showSpinButtons: true,
    },
  },
  severity: {
    dataField: 'severity',
    label: 'Severity',
    editorType: 'SelectBox',
    editorOptions: {
      items: severity,
      displayExpr: 'label',
      valueExpr: 'key',
      showClearButton: true,
    },
  },
  riskScore: {
    dataField: 'riskScore',
    label: 'Risk Score',
    editorType: 'NumberBox',
    editorOptions: {
      readOnly: true,
      format: '#0.#', // temporary fix until backend is updated to round to 2 decimals properly
    },
    isRequired: false,
  },
  specification: {
    dataField: 'specification',
    label: 'Specification',
    editorType: 'TextArea',
    isRequired: false,
  },
  usefulLife: {
    dataField: 'usefulLife',
    label: 'Useful Life (years)',
    editorType: 'NumberBox',
    isRequired: false,
    editorOptions: {
      format: '#0.#',
      min: 0,
      showSpinButtons: true,
    },
  },
  inUseStatus: {
    dataField: 'inUseStatus',
    label: 'In-use Status',
    isRequired: true,
    editorType: 'Lookup',
    editorOptions: {
      displayExpr: 'label',
      valueExpr: 'key',
      searchEnabled: false,
      dataSource: Object.values(inUseStatuses),
    },
  },
  thirdPartyId: {
    dataField: 'thirdPartyId',
    label: 'Third Party ID',
    editorType: 'TextBox',
    editorOptions: {
      readOnly: true,
    },
  },
  manufacturer: {
    dataField: 'manufacturer.name',
    label: 'Manufacturer',
  },
  assetType: {
    dataField: 'assetType.name',
    label: 'Model',
  },
  thirdPartySource: {
    dataField: 'thirdPartySource',
    label: 'Third Party Source',
    editorType: 'TextBox',
    editorOptions: {
      readOnly: true,
    },
  },
  picture: {
    dataField: 'picture',
    label: 'Image',
    editorType: 'Image',
    editorOptions: {
      alt: 'Selected image',
      height: '200px',
    },
    cssClass: 'hide-label',
  },
} satisfies FieldDefs<Model>;

export default fields;
